import React, { useEffect, useRef, useState } from "react";
import {
  Box, Flex, HStack, Text, Heading, Icon, Button, Image, Link, Input,
  Spinner, VStack, IconButton, Tooltip,
} from "@chakra-ui/react";
import {
  HiOutlineBookOpen, HiOutlinePencilSquare, HiOutlineCheck, HiOutlineXMark,
  HiOutlinePaperClip, HiOutlineArrowDownTray, HiOutlineTrash, HiOutlineSparkles,
} from "react-icons/hi2";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { c } from "../classroomColors";
import { brand, onTint } from "../standeTokens";
import { fileKind, fileIcon, fileTypeLabel } from "../fileTypes";
import { readingMinutes, partsSummary, isEmpty } from "./contentTokens";

const QUILL_TOOLBAR = [
  [{ header: [2, 3, false] }],
  ["bold", "italic", "underline"],
  [{ list: "ordered" }, { list: "bullet" }],
  ["blockquote", "link", "image"],
  ["clean"],
];
const QUILL_FORMATS = ["header", "bold", "italic", "underline", "list", "bullet", "blockquote", "link", "image"];

/** Shared prose styling, so the reader and the editor render identically and
 *  what a teacher writes is what a student sees. */
const proseStyles = {
  "& p": { marginBottom: "14px" },
  "& h1, & h2, & h3, & h4": { fontWeight: "600", color: c.text, marginTop: "26px", marginBottom: "10px", letterSpacing: "-0.01em" },
  "& h1": { fontSize: "21px" },
  "& h2": { fontSize: "18px" },
  "& h3": { fontSize: "16px" },
  "& ul, & ol": { paddingLeft: "24px", marginBottom: "14px" },
  "& li": { marginBottom: "7px" },
  "& a": { color: c.info, textDecoration: "underline" },
  "& strong, & b": { color: c.text, fontWeight: "600" },
  "& img": { maxWidth: "100%", borderRadius: "10px", margin: "16px 0" },
  "& blockquote": { borderLeft: `3px solid ${c.border}`, paddingLeft: "16px", color: c.textMuted, margin: "14px 0" },
};

const Empty = ({ isInstructor }) => (
  <Flex direction="column" align="center" justify="center" py="90px" gap="10px" textAlign="center">
    <Flex boxSize="46px" borderRadius="full" bg={c.surfaceSubtle} align="center" justify="center">
      <Icon as={HiOutlineBookOpen} boxSize="20px" color={c.textMuted} />
    </Flex>
    <Text fontSize="15px" fontWeight="500" color={c.text}>
      Pick a lesson
    </Text>
    <Text fontSize="13px" fontWeight="300" color={c.textSub} maxW="340px">
      {isInstructor
        ? "Choose one from the outline, or add a module to start building the course."
        : "Choose one from the outline to start reading."}
    </Text>
  </Flex>
);

const LessonPage = ({
  segment,
  moduleName,
  isInstructor,
  isDark,
  busy,
  onSaveBody,
  onUpload,
  onRemoveAttachment,
}) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef(null);
  const b = brand(isDark);
  const tint = onTint(isDark);

  // Moving to another lesson must not carry an unsaved draft across.
  useEffect(() => {
    setEditing(false);
    setDraft(segment?.overview_html || "");
  }, [segment?.guid, segment?.overview_html]);

  if (!segment) return <Empty isInstructor={isInstructor} />;

  const attachments = segment.course_segment_attachments || [];
  const media = attachments.filter((a) => fileKind(a.filename) !== "file");
  const files = attachments.filter((a) => fileKind(a.filename) === "file");
  const minutes = readingMinutes(segment);
  const summary = partsSummary(segment);
  const blank = isEmpty(segment);

  const save = async () => {
    setSaving(true);
    try {
      await onSaveBody(segment.guid, draft);
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  const handleFiles = (fileList) => {
    if (fileList?.length) onUpload(segment.guid, fileList);
  };

  return (
    <Box
      onDragOver={isInstructor ? (e) => { e.preventDefault(); setDragOver(true); } : undefined}
      onDragLeave={isInstructor ? () => setDragOver(false) : undefined}
      onDrop={isInstructor ? (e) => { e.preventDefault(); setDragOver(false); handleFiles(e.dataTransfer.files); } : undefined}
      position="relative"
      borderRadius="14px"
      outline={dragOver ? `2px dashed ${b.primaryBlue}` : "none"}
      outlineOffset="6px"
      transition="outline 0.15s"
    >
      {dragOver && (
        <Flex
          position="absolute" inset="0" zIndex={2} borderRadius="14px"
          bg={c.surface} opacity={0.94} align="center" justify="center" direction="column" gap="8px"
        >
          <Icon as={HiOutlinePaperClip} boxSize="22px" color={b.primaryBlue} />
          <Text fontSize="14px" fontWeight="500" color={c.text}>
            Drop to add to this lesson
          </Text>
        </Flex>
      )}

      {/* header */}
      <Flex justify="space-between" align="flex-start" gap="16px" mb="6px">
        <Box minW={0}>
          {moduleName && (
            <Text fontSize="11.5px" fontWeight="500" color={c.textMuted} letterSpacing="0.03em" mb="5px">
              {moduleName.toUpperCase()}
            </Text>
          )}
          <Heading fontSize="26px" fontWeight="500" color={c.text} letterSpacing="-0.02em" lineHeight="1.2">
            {segment.name}
          </Heading>
        </Box>

        {isInstructor && !editing && (
          <HStack spacing="7px" flexShrink={0}>
            <Button
              leftIcon={<Icon as={HiOutlinePaperClip} boxSize="14px" />}
              size="sm" h="33px" px="12px" borderRadius="8px" variant="outline"
              borderColor={c.border} color={c.textSub} fontSize="12.5px" fontWeight="400"
              _hover={{ bg: c.hover, color: c.text, borderColor: c.textMuted }}
              onClick={() => fileRef.current?.click()}
            >
              Attach
            </Button>
            <Button
              leftIcon={<Icon as={HiOutlinePencilSquare} boxSize="14px" />}
              size="sm" h="33px" px="12px" borderRadius="8px"
              bg={c.solidBg} color={c.solidText} fontSize="12.5px" fontWeight="500"
              _hover={{ opacity: 0.88 }}
              onClick={() => setEditing(true)}
            >
              {blank ? "Write this lesson" : "Edit"}
            </Button>
          </HStack>
        )}

        {editing && (
          <HStack spacing="7px" flexShrink={0}>
            <IconButton
              aria-label="Discard changes"
              icon={<Icon as={HiOutlineXMark} boxSize="16px" />}
              size="sm" h="33px" minW="33px" borderRadius="8px" variant="outline"
              borderColor={c.border} color={c.textSub}
              _hover={{ bg: c.hover, color: c.text }}
              onClick={() => { setDraft(segment.overview_html || ""); setEditing(false); }}
            />
            <Button
              leftIcon={<Icon as={HiOutlineCheck} boxSize="15px" />}
              size="sm" h="33px" px="13px" borderRadius="8px"
              bg={c.solidBg} color={c.solidText} fontSize="12.5px" fontWeight="500"
              isLoading={saving} loadingText="Saving"
              _hover={{ opacity: 0.88 }}
              onClick={save}
            >
              Save
            </Button>
          </HStack>
        )}
      </Flex>

      {/* the promise line: what this lesson holds */}
      {(summary || minutes) && !editing && (
        <HStack spacing="8px" mb="22px">
          {minutes && (
            <Text fontSize="12.5px" color={c.textMuted}>
              {minutes} min read
            </Text>
          )}
          {minutes && summary && <Box boxSize="3px" borderRadius="full" bg={c.textMuted} opacity={0.6} />}
          {summary && (
            <Text fontSize="12.5px" color={c.textMuted}>
              {summary}
            </Text>
          )}
        </HStack>
      )}
      {!summary && !minutes && !editing && <Box mb="22px" />}

      <Input ref={fileRef} type="file" multiple display="none"
        onChange={(e) => { handleFiles(e.target.files); e.target.value = ""; }} />

      {/* body */}
      {editing ? (
        <Box
          sx={{
            "& .ql-toolbar": {
              borderTopRadius: "10px", borderColor: c.border, background: c.surfaceSubtle,
            },
            "& .ql-container": {
              borderBottomRadius: "10px", borderColor: c.border, minHeight: "300px",
              fontSize: "14.5px", fontFamily: "inherit", color: c.text,
            },
            "& .ql-editor": { lineHeight: "1.75", padding: "18px" },
            "& .ql-editor.ql-blank::before": { color: c.textMuted, fontStyle: "normal" },
            "& .ql-stroke": { stroke: c.textSub }, "& .ql-fill": { fill: c.textSub },
            "& .ql-picker-label": { color: c.textSub },
          }}
        >
          <ReactQuill
            theme="snow"
            value={draft}
            onChange={setDraft}
            modules={{ toolbar: QUILL_TOOLBAR }}
            formats={QUILL_FORMATS}
            placeholder="Write the lesson. Headings, lists, links and images all work — or drag a file anywhere onto the page to attach it."
          />
        </Box>
      ) : segment.overview_html ? (
        <Box color={c.textSub} fontSize="15px" lineHeight="1.8" sx={proseStyles}
          dangerouslySetInnerHTML={{ __html: segment.overview_html }} />
      ) : (
        <Flex
          direction="column" align="center" justify="center" py="52px" gap="9px" textAlign="center"
          border="1px dashed" borderColor={c.border} borderRadius="12px" bg={c.surfaceSubtle}
        >
          <Icon as={HiOutlineSparkles} boxSize="19px" color={c.textMuted} />
          <Text fontSize="14px" fontWeight="500" color={c.text}>
            {isInstructor ? "This lesson is empty" : "Nothing here yet"}
          </Text>
          <Text fontSize="12.5px" fontWeight="300" color={c.textSub} maxW="330px">
            {isInstructor
              ? "Write it, or drop a PDF, slide deck or video straight onto this page."
              : "Your teacher hasn't written this lesson yet."}
          </Text>
        </Flex>
      )}

      {/* media */}
      {media.length > 0 && (
        <VStack align="stretch" spacing="16px" mt="26px">
          {media.map((att) => (
            <Box key={att.guid} position="relative" borderRadius="12px" overflow="hidden"
              border="1px solid" borderColor={c.borderSoft} bg={c.surfaceSubtle} p="10px">
              {fileKind(att.filename) === "image" ? (
                <Image src={att.s3_url} alt={att.filename} maxH="440px" width="100%"
                  objectFit="contain" borderRadius="8px" />
              ) : (
                <video src={att.s3_url} controls
                  style={{ width: "100%", maxHeight: "470px", borderRadius: "8px", display: "block" }} />
              )}
              {isInstructor && (
                <Tooltip label="Remove" placement="left" hasArrow fontSize="11px">
                  <IconButton
                    aria-label={`Remove ${att.filename}`}
                    icon={<Icon as={HiOutlineTrash} boxSize="14px" />}
                    size="xs" position="absolute" top="16px" right="16px"
                    borderRadius="7px" bg={c.surface} color={c.textSub}
                    border="1px solid" borderColor={c.border}
                    _hover={{ bg: b.lightRed, color: tint.lightRed }}
                    onClick={() => onRemoveAttachment(att.guid)}
                  />
                </Tooltip>
              )}
            </Box>
          ))}
        </VStack>
      )}

      {/* files — a short list of chips rather than full-bleed rows: a file
          name is short, and a row the width of the page reads as a mistake. */}
      {files.length > 0 && (
        <Box mt="30px" maxW="460px">
          <Text fontSize="11.5px" fontWeight="600" color={c.textMuted} letterSpacing="0.05em" mb="11px">
            RESOURCES
          </Text>
          <VStack align="stretch" spacing="7px">
            {files.map((att) => (
              <Flex
                key={att.guid} align="center" gap="11px" px="11px" py="9px"
                border="1px solid" borderColor={c.borderSoft} borderRadius="10px"
                bg={c.surface} transition="all 0.15s"
                _hover={{ bg: c.hover, borderColor: c.textMuted }}
              >
                <Flex flexShrink={0} boxSize="30px" align="center" justify="center"
                  borderRadius="8px" bg={c.surfaceSubtle}>
                  <Icon as={fileIcon(att.filename)} boxSize="16px" color={c.text} />
                </Flex>
                <Box flex="1" minW={0}>
                  <Text fontSize="13px" fontWeight="500" color={c.text} noOfLines={1}>
                    {att.filename}
                  </Text>
                  <Text fontSize="11px" color={c.textMuted}>
                    {fileTypeLabel(att.filename)}
                  </Text>
                </Box>
                <Link href={att.s3_url} isExternal _hover={{ textDecoration: "none" }} flexShrink={0}>
                  <Icon as={HiOutlineArrowDownTray} boxSize="16px" color={c.textSub} />
                </Link>
                {isInstructor && (
                  <IconButton
                    aria-label={`Remove ${att.filename}`}
                    icon={<Icon as={HiOutlineTrash} boxSize="14px" />}
                    size="xs" variant="ghost" borderRadius="7px" color={c.textMuted} flexShrink={0}
                    _hover={{ bg: b.lightRed, color: tint.lightRed }}
                    onClick={() => onRemoveAttachment(att.guid)}
                  />
                )}
              </Flex>
            ))}
          </VStack>
        </Box>
      )}

      {busy && (
        <HStack spacing="8px" mt="18px" justify="center">
          <Spinner size="xs" color={c.accent} />
          <Text fontSize="12px" color={c.textMuted}>Saving…</Text>
        </HStack>
      )}
    </Box>
  );
};

export default LessonPage;
